import React, { useCallback, useMemo } from 'react';

import { useAppDispatch } from '../../../../hooks/react-redux';
import { DeleteButton } from '../../../shared/button/DeleteButton';
import { PlusButton } from '../../../shared/button/PlusButton';
import { InformationTooltip } from '../../../shared/tooltip';
import styles from '../DocumentName.module.scss';
import { addNewAIDiscoverItem, deleteAIDiscoverItem, DocumentNameAIDiscoverKey, SystemDocumentNameDB, updateAIDiscoverItem } from '../store';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { AutoResizeLongText } from '../../../shared/longtext/AutoResizeLongText';

interface AIDiscoverProps {
    systemDocumentName: SystemDocumentNameDB;
}

interface DiscoverColumn {
    key: DocumentNameAIDiscoverKey;
    header: string;
    description: string;
}

export const AIDiscover: React.FC<AIDiscoverProps> = ({ systemDocumentName }) => {
    const dispatch = useAppDispatch();
    const addItem = useCallback((key: DocumentNameAIDiscoverKey) => dispatch(addNewAIDiscoverItem(key)), [dispatch]);
    const updateItemName = useCallback((key: DocumentNameAIDiscoverKey, index: number, value: string) => dispatch(updateAIDiscoverItem(key, index, value)), [dispatch]);
    const deleteItem = useCallback((key: DocumentNameAIDiscoverKey, index: number) => dispatch(deleteAIDiscoverItem(key, index)), [dispatch]);

    const discoverColumns: DiscoverColumn[] = useMemo(() => [
        {
            key: 'potentialDocumentNames',
            header: 'Document Titles',
            description: 'The potential title that may represent this document type. For example: "ISDA MA", "ISDA Master Agreement", "Master Agreement", "2002 Master Agreement".'
        },
        {
            key: 'documentKeyWords',
            header: 'Key Giveaways',
            description: 'Distinguishing key words or phrases that could be used to narrow down the exact document type. For example: "1987" to distinguish which ISDA, "transferor" to distinguish between English vs New York Law CSA.'
        },
        {
            key: 'documentExcludedWords',
            header: 'Excluded Terms',
            description: 'Anything which would make it explicitly not this document name. For example: "transferor" for a New York Law CSA. Note: When post processing to discover exactly what document name to link to the document that is being analysed, none of the words/phrases found here should be found within the document.'
        }
    ], []);

    const getColumnContent = useCallback((key: DocumentNameAIDiscoverKey) => systemDocumentName[key] || [], [systemDocumentName]);

    return (
        <div className={styles.inputsWrapper}>
            <div className={styles.discoverWrapper}>
                {discoverColumns.map(({ key, header, description }) => (
                    <div className={styles.discoverColumnWrapper} key={key}>
                        <div className={styles.headerWrapper}>
                            <div className={styles.headerLabel}>{header}</div>
                            <div className={styles.descriptionWrapper}><InformationTooltip content={description} /></div>
                            <div className={styles.addNewWrapper}><PlusButton onClick={() => addItem(key)} fontSize={15} /></div>
                        </div>
                        <div className={styles.listItems}>
                            <Scrollable>
                                {getColumnContent(key).map((value, index) => (
                                    <div className={styles.listItemWrapper} key={`${key}-${index}`}>
                                        <AutoResizeLongText
                                            className={styles.listItemInput}
                                            value={value}
                                            updateValue={value => updateItemName(key, index, value)}
                                            testId={`document-name-ai-discover-${key}-item-input-${index}`}
                                            maxLength={1000}
                                        />
                                        <DeleteButton onClick={() => deleteItem(key, index)} fontSize={15} />
                                    </div>
                                ))}
                            </Scrollable>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
